import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import PropTypes from 'prop-types';
import AvatarEditor from 'react-avatar-editor';
import { get, set } from '@totalsoft/change-tracking-react';
import { DropzoneAreaBase } from 'mui-file-dropzone';
import { Button, CollapseCard } from '@totalsoft/rocket-ui';
import PersonSymbol from '@mui/icons-material/Person';
import { env } from 'utils/env';
import { useParams } from 'hooks';

const userAccountUrl = `${env.REACT_APP_IDENTITY_AUTHORITY}/Identity/Account/Manage`;
const userChangePasswordUrl = `${env.REACT_APP_IDENTITY_AUTHORITY}/Identity/Account/Manage/ChangePassword`;

const handleOpenInNewTab = url => event => {
  const win = window.open(url, '_blank');
  win.focus();
  event.preventDefault();
};

const UserInfoComponent = ({ profileDataLens, personData, toggleExpanded, expanded, onSaveUserInfo, editor, saving, isDirtyInfo }) => {
  const { t } = useTranslation();
  const profileData = profileDataLens |> get;
  const enableUserAccountManagement = useParams('EnableUserAccountManagement');

  return (
    <CollapseCard
      id='UserInfo_IconCard'
      icon={PersonSymbol}
      title={t('Profile.User')}
      expanded={expanded}
      onToggle={toggleExpanded}
      toggleOnHeaderClick
    >
      <Grid container direction='row'>
        <Grid item container xs={12} md={6} xl={3} alignContent='flex-start'>
          <Grid item xs={12}>
            <strong>{t('Profile.FirstName')}:</strong> <span>{personData.firstName}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('Profile.LastName')}:</strong> <span>{personData.lastName}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('Profile.MobilePhone')}:</strong> <span>{personData.mobilePhone}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('Profile.Email')}:</strong> <span>{personData.e_Mail}</span>
          </Grid>

          <Grid item xs={12}>
            <strong>{t('Profile.Position')}:</strong> <span>{personData.positionName}</span>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          {profileData?.avatar ? (
            <>
              {isDirtyInfo ? (
                <AvatarEditor
                  ref={editor}
                  image={profileData.avatar}
                  width={200}
                  height={193}
                  color={[255, 255, 255, 0.6]}
                  scale={1.2}
                  rotate={0}
                  borderRadius={120}
                />
              ) : (
                <img style={{ maxWidth: 250, maxHeigth: 200 }} src={profileData.avatar} alt='profile-pic'></img>
              )}
            </>
          ) : (
            <DropzoneAreaBase
              alignContent='flex-end'
              acceptedFiles={['image/*']}
              dropzoneClass={{ maxWidth: 250, maxHeigth: 200 }}
              filesLimit={1}
              maxFileSize={Number.MAX_VALUE}
              onAdd={files => (profileDataLens.avatar |> set)(files[0].data)}
              onDrag={files => (profileDataLens.avatar |> set)(files[0].data)}
            />
          )}
        </Grid>
        <Grid container justifyContent='flex-start' spacing={1} gap={1} mt={1}>
          {enableUserAccountManagement && (
            <>
              <Button variant='contained' onClick={handleOpenInNewTab(userAccountUrl)}>
                {t('Profile.MyAccount')}
              </Button>
              <Button variant='contained' onClick={handleOpenInNewTab(userChangePasswordUrl)}>
                {t('Profile.ChangePassword')}
              </Button>
            </>
          )}
          <Button
            variant='outlined'
            onClick={() => (profileDataLens.avatar |> set)(null)}
            disabled={!profileData.avatar || isDirtyInfo || saving}
          >
            {t('Profile.ClearPicture')}
          </Button>
          <Button variant='contained' onClick={() => onSaveUserInfo(editor)} disabled={saving || !isDirtyInfo}>
            {saving ? t('Saving') : t('Save')}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

UserInfoComponent.propTypes = {
  profileDataLens: PropTypes.object,
  personData: PropTypes.object.isRequired,
  toggleExpanded: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onSaveUserInfo: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  isDirtyInfo: PropTypes.bool.isRequired,
  editor: PropTypes.object
};

export default UserInfoComponent;
